import { inject, Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable } from 'rxjs';
import { DateManipulationCheckInterface } from '../../interfaces/date-manipulation-check.interface';
import {
    externalTimestampReceived,
    timestampCheckRequested,
} from '../../state/date-manipulation-check/actions/date-manipulation-check.actions';
import { momentToTimestampState } from '../../state/date-manipulation-check/date-manipulation-check.converters';
import {
    dispatchDateManipulationCheckInit,
    dispatchExternalTimestampWrite,
} from '../../state/date-manipulation-check/date-manipulation-check-write-bridge';
import {
    selectDateManipulationCheckInitialized,
    selectLastTimestampCheckValid,
} from '../../state/date-manipulation-check/selectors/date-manipulation-check.selector';

/**
 * NgRx-backed implementation of {@link DateManipulationCheckInterface}. A thin translation layer
 * only: the validity policy (tolerance, both-baselines-valid conjunction, advancing the internal
 * timestamp) lives entirely in `dateManipulationCheckTimestampReducer`; this service dispatches
 * and reads the result back. Reads-after-dispatch are safe because reducers run synchronously
 * inside `dispatch()`.
 *
 * Two deliberate fidelity deltas from the legacy `DateManipulationCheckService`:
 *
 * 1. `checkTimestamp()` resolves immediately after the dispatch, whereas the legacy service
 *    awaited the storage write before resolving. Immaterial for a boolean sync gate — the
 *    validity outcome is already decided synchronously by the reducer; only the storage mirror
 *    trails behind.
 * 2. The two timestamps persist to the cross-device concurrency store (via `persistSlice`)
 *    instead of the legacy device-local encrypted `localStorage`. See
 *    `docs/date-manipulation-check-ngrx-migration.md`, Decision 1.
 */
@Injectable({
    providedIn: 'root',
})
export class DateManipulationCheckNgrxService implements DateManipulationCheckInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    /** Monotonic counter for write correlation ids (no caller awaits a concurrent write). */
    private writeCounter = 0;

    public get isInitialized(): boolean {
        return this.store.selectSignal(selectDateManipulationCheckInitialized)();
    }

    public init(): Observable<unknown> {
        return dispatchDateManipulationCheckInit(this.store, this.actions$);
    }

    public async checkTimestamp(timestamp: moment.Moment): Promise<boolean> {
        this.assertInitialized('checkTimestamp');
        this.store.dispatch(timestampCheckRequested({ timestamp: momentToTimestampState(timestamp) }));
        return this.store.selectSignal(selectLastTimestampCheckValid)();
    }

    public setExternalTimestamp(timestamp: moment.Moment): Observable<void> {
        this.assertInitialized('setExternalTimestamp');
        const correlationId = `date-manipulation-external-${++this.writeCounter}`;
        return dispatchExternalTimestampWrite(
            this.store,
            this.actions$,
            externalTimestampReceived({ timestamp: momentToTimestampState(timestamp), correlationId }),
            correlationId,
        );
    }

    private assertInitialized(methodName: string): void {
        if (!this.isInitialized) {
            throw new Error(
                `DateManipulationCheckService: ${methodName}: Trying to call DateManipulationCheckService before initialization!`,
            );
        }
    }
}
